import React, { useState, useEffect } from 'react';
import {
    Copy,
    Users,
    TrendingUp,
    DollarSign,
    Award,
    ChevronDown,
    ChevronUp,
    ExternalLink,
    RefreshCw,
    Zap,
    Link,
    UserPlus
} from "lucide-react";
import {
    Tooltip,
    CircularProgress,
    Avatar,
    Chip,
    Snackbar,
    Alert,
    Box,
    Typography,
    Card,
    CardContent,
    LinearProgress,
    IconButton,
    Badge
} from "@mui/material";
import { useSelector, useDispatch } from 'react-redux';
import { userSelect, homeScreenStatSelect, allRemoteSettingsSelect } from '../../../redux/selectors/index';
import { authActions } from '../../../redux/actions';
import { capitalizeFirstLetter, niceDate, money, guessAdminRoute } from '../../../utility/Helper1';
import WrapUpP2p from '../p2p/layouts/WrapUpP2p';

export default function AffiliateDashboard() {
    const dispatch = useDispatch();
    const site_settings = useSelector(allRemoteSettingsSelect);
    const userData = useSelector(userSelect);
    const finance = useSelector(homeScreenStatSelect);

    const [isLoading, setIsLoading] = useState(true);
    const [showUpline, setShowUpline] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [viewingLevel, setViewingLevel] = useState(1);
    const [refData, setRefData] = useState([]);
    const [refDataRaw, setRefDataRaw] = useState([]);
    const [mainUplineData, setMainUplineData] = useState({});
    const [lengthToShow, setLengthToShow] = useState(2000);
    const [activeTab, setActiveTab] = useState('downlines');

    const myRefCode = userData?.myrefid;
    const myRefLink = window.location.origin + '/#/signup?ref=' + myRefCode;

    const loadDownLines = (myrefid, level) => {
        setIsLoading(true);
        dispatch(authActions.onRefreshSystemData(`user/referral/${myrefid}`, 10, {}, response => {
            setIsLoading(false);
            setRefDataRaw(response?.data);
            setRefData(response?.data?.referrals);
            setMainUplineData(response?.data?.upline);
        }));
    };

    useEffect(() => {
        loadDownLines(userData?.myrefid, 1);
    }, []);

    const copyToClipboard = () => {
        navigator.clipboard.writeText(myRefLink)
            .then(() => {
                // setSnackbarMessage("Referral link copied to clipboard!");
                // setSnackbarSeverity("success");
                // setSnackbarOpen(true);
            })
            .catch(() => {
                setSnackbarMessage("Failed to copy referral link");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            });
    };

    const showMore = () => {
        setLengthToShow(el => el + 20);
    };

    const originalDataLength = refData?.['level' + viewingLevel] || [];
    const allReferralData = originalDataLength?.slice(0, lengthToShow);

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    const refreshData = () => {
        loadDownLines(userData?.myrefid, viewingLevel);
    };

    const commissionAmount = finance?.[0]?.commission ?? 0;
    const hasCommission = commissionAmount > 0;
    const currencyType = finance?.[0]?.type;

    // Calculate total earnings and referral counts
    const totalReferrals = originalDataLength?.length || 0;
    const activeReferrals = originalDataLength?.filter(ref => ref.status === 'active' || true)?.length || 0;

    const totalEarnings = allReferralData?.reduce((sum, downline) => {
        const refWithdrawnFromMe = Number(downline?.['refWithdrawnFromMe_' + viewingLevel]) ?? 0;
        const rebWithdrawnFromMe = Number(downline?.['rebateWithdrawnFromMe_' + viewingLevel]) ?? 0;
        return sum + refWithdrawnFromMe + rebWithdrawnFromMe;
    }, 0);

    return (
        <WrapUpP2p>
            <div className="min-h-screen bg-black p-4 md:p-8" style={{
                background: 'linear-gradient(135deg, #000000 0%, #0a0a1f 100%)',
                backgroundAttachment: 'fixed'
            }}>
                <div className="max-w-7xl mx-auto">
                    {/* Header Section */}
                    <div className="mb-8 text-center">
                        <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{
                            color: '#fff',
                            textShadow: '0 0 10px rgba(123, 97, 255, 0.8), 0 0 20px rgba(123, 97, 255, 0.5)'
                        }}>
                            Affiliate Dashboard
                        </h1>
                        <p className="text-gray-300">Grow your network and boost your earnings</p>
                    </div>

                    {/* Stats Cards Section */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                        {/* Commission Card */}
                        <Card sx={{
                            borderRadius: '16px',
                            background: 'linear-gradient(135deg, rgba(41, 21, 94, 0.9) 0%, rgba(88, 28, 135, 0.8) 100%)',
                            boxShadow: '0 8px 32px rgba(123, 97, 255, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            overflow: 'hidden',
                            position: 'relative'
                        }}>
                            <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '2px',
                                background: 'linear-gradient(90deg, #4f46e5, #a855f7, #4f46e5)',
                                backgroundSize: '200% 100%',
                                animation: 'gradientMove 3s linear infinite',
                                '@keyframes gradientMove': {
                                    '0%': { backgroundPosition: '0% 0' },
                                    '100%': { backgroundPosition: '200% 0' },
                                }
                            }} />
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <DollarSign
                                        size={24}
                                        style={{
                                            color: '#a855f7',
                                            filter: 'drop-shadow(0 0 8px rgba(168, 85, 247, 0.7))'
                                        }}
                                    />
                                    <Typography variant="subtitle2" sx={{ ml: 1, color: '#d1d5db' }}>
                                        Total Commission
                                    </Typography>
                                </Box>
                                <Typography variant="h4" sx={{
                                    fontWeight: 'bold',
                                    color: '#fff',
                                    textShadow: '0 0 10px rgba(168, 85, 247, 0.6)'
                                }}>
                                    {money(commissionAmount, currencyType)}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mt: 1,
                                        backgroundColor: 'rgba(168, 85, 247, 0.2)',
                                        borderRadius: '8px',
                                        padding: '4px 8px'
                                    }}
                                >
                                    <TrendingUp size={16} style={{ color: '#a855f7' }} />
                                    <Typography variant="caption" sx={{ ml: 0.5, color: '#a855f7' }}>
                                        Earn more with each referral
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Total Referrals Card */}
                        {/* <Card sx={{
                            borderRadius: '16px',
                            background: 'linear-gradient(135deg, rgba(21, 66, 94, 0.9) 0%, rgba(28, 103, 135, 0.8) 100%)',
                            boxShadow: '0 8px 32px rgba(56, 189, 248, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            overflow: 'hidden',
                            position: 'relative'
                        }}>
                            <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '2px',
                                background: 'linear-gradient(90deg, #0ea5e9, #38bdf8, #0ea5e9)',
                                backgroundSize: '200% 100%',
                                animation: 'gradientMove 3s linear infinite',
                            }} />
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Users
                                        size={24}
                                        style={{
                                            color: '#38bdf8',
                                            filter: 'drop-shadow(0 0 8px rgba(56, 189, 248, 0.7))'
                                        }}
                                    />
                                    <Typography variant="subtitle2" sx={{ ml: 1, color: '#d1d5db' }}>
                                        Total Referrals
                                    </Typography>
                                </Box>
                                <Typography variant="h4" sx={{
                                    fontWeight: 'bold',
                                    color: '#fff',
                                    textShadow: '0 0 10px rgba(56, 189, 248, 0.6)'
                                }}>
                                    {totalReferrals}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mt: 1,
                                        backgroundColor: 'rgba(56, 189, 248, 0.2)',
                                        borderRadius: '8px',
                                        padding: '4px 8px'
                                    }}
                                >
                                    <Badge
                                        color="success"
                                        variant="dot"
                                        sx={{ '& .MuiBadge-dot': { backgroundColor: '#10b981' } }}
                                    >
                                        <Users size={16} style={{ color: '#38bdf8' }} />
                                    </Badge>
                                    <Typography variant="caption" sx={{ ml: 0.5, color: '#38bdf8' }}>
                                        {activeReferrals} active users
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card> */}

                        {/* Total Earnings Card */}
                        <Card sx={{
                            borderRadius: '16px',
                            background: 'linear-gradient(135deg, rgba(21, 94, 51, 0.9) 0%, rgba(28, 135, 66, 0.8) 100%)',
                            boxShadow: '0 8px 32px rgba(52, 211, 153, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            overflow: 'hidden',
                            position: 'relative'
                        }}>
                            <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '2px',
                                background: 'linear-gradient(90deg, #10b981, #34d399, #10b981)',
                                backgroundSize: '200% 100%',
                                animation: 'gradientMove 3s linear infinite',
                            }} />
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Award
                                        size={24}
                                        style={{
                                            color: '#34d399',
                                            filter: 'drop-shadow(0 0 8px rgba(52, 211, 153, 0.7))'
                                        }}
                                    />
                                    <Typography variant="subtitle2" sx={{ ml: 1, color: '#d1d5db' }}>
                                        Uplines Earnings
                                    </Typography>
                                </Box>
                                <Typography variant="h4" sx={{
                                    fontWeight: 'bold',
                                    color: '#fff',
                                    textShadow: '0 0 10px rgba(52, 211, 153, 0.6)'
                                }}>
                                    {money(totalEarnings, currencyType)}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mt: 1,
                                        backgroundColor: 'rgba(52, 211, 153, 0.2)',
                                        borderRadius: '8px',
                                        padding: '4px 8px'
                                    }}
                                >
                                    <Zap size={16} style={{ color: '#34d399' }} />
                                    <Typography variant="caption" sx={{ ml: 0.5, color: '#34d399' }}>
                                        From your activity
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>

                    </div>

                    {/* Referral Link Card */}
                    <Card sx={{
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(30, 41, 59, 0.8) 100%)',
                        boxShadow: '0 8px 32px rgba(148, 163, 184, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        mb: 4,
                        overflow: 'hidden',
                        position: 'relative'
                    }}>
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '3px',
                            background: 'linear-gradient(90deg, #4f46e5, #a855f7, #ec4899, #4f46e5)',
                            backgroundSize: '300% 100%',
                            animation: 'gradientMove 6s linear infinite',
                        }} />
                        <CardContent sx={{ p: 3 }}>
                            <Box display="flex" alignItems="center" mb={2}>
                                <Link
                                    size={24}
                                    style={{
                                        color: '#a855f7',
                                        filter: 'drop-shadow(0 0 8px rgba(168, 85, 247, 0.7))'
                                    }}
                                />
                                <Typography variant="h6" sx={{ ml: 1, color: '#f3f4f6', fontWeight: 'bold' }}>
                                    Your Referral Link
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 1.75,
                                    borderRadius: '12px',
                                    background: 'rgba(15, 23, 42, 0.7)',
                                    border: '1px solid rgba(255, 255, 255, 0.05)',
                                    position: 'relative',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        inset: '-1px',
                                        borderRadius: '13px',
                                        padding: '1px',
                                        background: 'linear-gradient(90deg, #4f46e5, #a855f7, #ec4899)',
                                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                        WebkitMaskComposite: 'xor',
                                        maskComposite: 'exclude',
                                    }
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: '#d1d5db',
                                        flex: 1,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        fontFamily: 'monospace',
                                        pl: 1
                                    }}
                                >
                                    {myRefLink}
                                </Typography>
                                <Tooltip title="Copy to clipboard" arrow>
                                    <IconButton
                                        onClick={copyToClipboard}
                                        sx={{
                                            color: '#a855f7',
                                            '&:hover': {
                                                backgroundColor: 'rgba(168, 85, 247, 0.1)',
                                            }
                                        }}
                                    >
                                        <Copy size={18} />
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            <Typography variant="body2" sx={{ mt: 2, color: '#9ca3af' }}>
                                Share this link with friends and earn commissions when they sign up and start trading. The more people you refer, the more you earn!
                            </Typography>
                        </CardContent>
                    </Card>

                    {/* Tabs Navigation */}
                    <Box sx={{
                        display: 'flex',
                        borderRadius: '16px',
                        background: 'rgba(15, 23, 42, 0.8)',
                        p: 0.6,
                        mb: 3,
                        maxWidth: '400px',
                        marginLeft: 'auto',
                        marginRight: 'auto'
                    }}>
                        <Box
                            onClick={() => setActiveTab('downlines')}
                            sx={{
                                flex: 1,
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                borderRadius: '12px',
                                backgroundColor: activeTab === 'downlines' ? 'rgba(79, 70, 229, 0.2)' : 'transparent',
                                color: activeTab === 'downlines' ? '#fff' : '#9ca3af',
                                transition: 'all 0.2s ease',
                                fontSize: '14px',
                                fontWeight: 'medium',
                                '&:hover': {
                                    backgroundColor: activeTab !== 'downlines' ? 'rgba(79, 70, 229, 0.1)' : 'rgba(79, 70, 229, 0.2)',
                                }
                            }}
                        >
                            <Users size={18} style={{ marginRight: '8px' }} />
                            Downlines
                        </Box>
                        {mainUplineData && Object.keys(mainUplineData).length > 0 && (
                            <Box
                                onClick={() => setActiveTab('upline')}
                                sx={{
                                    flex: 1,
                                    p: 1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    borderRadius: '12px',
                                    backgroundColor: activeTab === 'upline' ? 'rgba(168, 85, 247, 0.2)' : 'transparent',
                                    color: activeTab === 'upline' ? '#fff' : '#9ca3af',
                                    transition: 'all 0.2s ease',
                                    fontSize: '14px',
                                    fontWeight: 'medium',
                                    '&:hover': {
                                        backgroundColor: activeTab !== 'upline' ? 'rgba(168, 85, 247, 0.1)' : 'rgba(168, 85, 247, 0.2)',
                                    }
                                }}
                            >
                                <UserPlus size={18} style={{ marginRight: '8px' }} />
                                Upline
                            </Box>
                        )}
                    </Box>

                    {/* Content Area */}
                    {activeTab === 'downlines' ? (
                        <Card sx={{
                            borderRadius: '16px',
                            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(30, 41, 59, 0.8) 100%)',
                            boxShadow: '0 8px 32px rgba(148, 163, 184, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.05)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            overflow: 'hidden',
                            position: 'relative'
                        }}>
                            <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '3px',
                                background: 'linear-gradient(90deg, #4f46e5, #38bdf8, #4f46e5)',
                                backgroundSize: '200% 100%',
                                animation: 'gradientMove 3s linear infinite',
                            }} />

                            <CardContent sx={{ p: 3 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
    <Box display="flex" alignItems="center">
        <Users
            size={22}
            style={{
                color: '#38bdf8',
                filter: 'drop-shadow(0 0 8px rgba(56, 189, 248, 0.7))'
            }}
        />
        <Typography variant="h6" sx={{ ml: 1, color: '#f3f4f6', fontWeight: 'bold' }}>
            Show Level
        </Typography>
        <Box display="flex" ml={2} gap={1}>
            {[1, 2, 3].map((level) => (
                <Tooltip key={level} title={`Level ${level}`} arrow>
                    <IconButton
                        size="small"
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            backgroundColor: level == viewingLevel ? 'rgba(56, 189, 248, 0.15)' : 'transparent',
                            border: '1px solid #38bdf8',
                            color: '#38bdf8',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            '&:hover': {
                                backgroundColor: 'rgba(56, 189, 248, 0.25)',
                            },
                            transition: 'all 0.2s ease-in-out'
                        }}
                        onClick={() => setViewingLevel(level)}
                    >
                        {level}
                    </IconButton>
                </Tooltip>
            ))}
        </Box>
    </Box>
    <Tooltip title="Refresh data" arrow>
        <IconButton
            onClick={refreshData}
            sx={{
                color: '#38bdf8',
                '&:hover': {
                    backgroundColor: 'rgba(56, 189, 248, 0.1)',
                }
            }}
        >
            <RefreshCw size={18} />
        </IconButton>
    </Tooltip>
</Box>

                                {isLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                                        <CircularProgress
                                            size={40}
                                            sx={{
                                                color: '#38bdf8',
                                                '& .MuiCircularProgress-circle': {
                                                    strokeLinecap: 'round',
                                                }
                                            }}
                                        />
                                    </Box>
                                ) : allReferralData?.length > 0 ? (
                                    <Box sx={{
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        background: 'rgba(15, 23, 42, 0.5)',
                                        border: '1px solid rgba(255, 255, 255, 0.05)',
                                    }}>
                                        <Box sx={{ overflowX: 'auto' }}>
                                            <table style={{
                                                width: '100%',
                                                borderCollapse: 'collapse',
                                                color: '#e5e7eb'
                                            }}>
                                                <thead>
                                                    <tr style={{
                                                        backgroundColor: 'rgba(30, 41, 59, 0.7)',
                                                        textAlign: 'left'
                                                    }}>
                                                        <th style={{ padding: '16px 20px', color: '#9ca3af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                            User Details
                                                        </th>
                                                        <th style={{ padding: '16px 20px', color: '#9ca3af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>
                                                            Your Earning
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {allReferralData.map((downline, index) => {
                                                        let refWithdrawnFromMe = downline?.['refWithdrawnFromMe_' + viewingLevel] ?? 0;
                                                        let rebWithdrawnFromMe = downline?.['rebateWithdrawnFromMe_' + viewingLevel] ?? 0;
                                                        let totalEarning = refWithdrawnFromMe + rebWithdrawnFromMe;

                                                        return (
                                                            <tr key={downline.id} style={{
                                                                borderTop: '1px solid rgba(255, 255, 255, 0.05)',
                                                                transition: 'background-color 0.2s',
                                                                backgroundColor: index % 2 === 0 ? 'rgba(30, 41, 59, 0.3)' : 'transparent'
                                                            }}>
                                                                <td style={{ padding: '16px 20px' }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                        <Avatar
                                                                            src={`${guessAdminRoute()}/assets/uploads/avatar/${downline?.image}`}
                                                                            alt={downline?.username}
                                                                            sx={{
                                                                                width: 44,
                                                                                height: 44,
                                                                                border: '2px solid rgba(56, 189, 248, 0.3)',
                                                                                boxShadow: '0 0 10px rgba(56, 189, 248, 0.3)'
                                                                            }}
                                                                        />
                                                                        <Box sx={{ ml: 2 }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                                <Typography sx={{
                                                                                    fontWeight: 'bold',
                                                                                    fontSize: '14px',
                                                                                    color: '#f3f4f6'
                                                                                }}>
                                                                                    {capitalizeFirstLetter(downline?.username)}
                                                                                </Typography>
                                                                                <Chip
                                                                                    label="Active"
                                                                                    size="small"
                                                                                    sx={{
                                                                                        ml: 1,
                                                                                        height: '20px',
                                                                                        fontSize: '10px',
                                                                                        borderRadius: '4px',
                                                                                        backgroundColor: 'rgba(16, 185, 129, 0.2)',
                                                                                        color: '#10b981',
                                                                                        border: '1px solid rgba(16, 185, 129, 0.3)',
                                                                                        '& .MuiChip-label': {
                                                                                            padding: '0 6px',
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                            <Typography sx={{
                                                                                fontSize: '12px',
                                                                                color: '#9ca3af',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                mt: 0.5
                                                                            }}>
                                                                                <span style={{
                                                                                    display: 'inline-block',
                                                                                    width: '8px',
                                                                                    height: '8px',
                                                                                    borderRadius: '50%',
                                                                                    backgroundColor: 'rgba(107, 114, 128, 0.5)',
                                                                                    marginRight: '6px'
                                                                                }}></span>
                                                                                Joined {niceDate(downline?.date)}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                </td>
                                                                <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                                        <Typography sx={{
                                                                            fontWeight: 'bold',
                                                                            fontSize: '16px',
                                                                            color: totalEarning > 0 ? '#10b981' : '#e5e7eb'
                                                                        }}>
                                                                            {money(totalEarning, finance?.[0]?.type)}
                                                                        </Typography>
                                                                        {totalEarning > 0 && (
                                                                            <Typography sx={{
                                                                                fontSize: '12px',
                                                                                color: 'rgba(16, 185, 129, 0.8)',
                                                                                mt: 0.5
                                                                            }}>
                                                                                +{money(totalEarning, finance?.[0]?.type, { removeSymbol: true })} earned
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </Box>

                                        {(originalDataLength?.length ?? 0) > lengthToShow && (
                                            <Box sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                py: 3,
                                                borderTop: '1px solid rgba(255, 255, 255, 0.05)'
                                            }}>
                                                <button
                                                    onClick={showMore}
                                                    style={{
                                                        padding: '10px 24px',
                                                        borderRadius: '8px',
                                                        border: 'none',
                                                        background: 'linear-gradient(90deg, #4f46e5, #38bdf8)',
                                                        color: 'white',
                                                        fontWeight: '600',
                                                        fontSize: '14px',
                                                        cursor: 'pointer',
                                                        boxShadow: '0 4px 10px rgba(56, 189, 248, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05), 0 0 40px rgba(79, 70, 229, 0.2) inset',
                                                        transition: 'all 0.2s',
                                                        position: 'relative',
                                                        overflow: 'hidden',
                                                        '&::before': {
                                                            content: '""',
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            background: 'rgba(255, 255, 255, 0.1)',
                                                            transform: 'translateX(-100%) rotate(45deg)',
                                                            transition: 'transform 0.5s',
                                                        },
                                                        '&:hover': {
                                                            transform: 'translateY(-2px)',
                                                            boxShadow: '0 6px 15px rgba(56, 189, 248, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1), 0 0 40px rgba(79, 70, 229, 0.3) inset',
                                                        },
                                                        '&:hover::before': {
                                                            transform: 'translateX(100%) rotate(45deg)',
                                                        },
                                                        '&:active': {
                                                            transform: 'translateY(0px)',
                                                        },
                                                    }}>LoadMore
                                                </button>
                                            </Box>
                                        )}
                                    </Box>
                                ) : (
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        py: 8,
                                        textAlign: 'center'
                                    }}>
                                        <Users size={48} style={{ color: '#4b5563', marginBottom: '16px' }} />
                                        <Typography variant="h6" sx={{ color: '#9ca3af', fontWeight: 'bold', mb: 1 }}>
                                            No Downlines Yet
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#6b7280', maxWidth: '400px' }}>
                                            You haven't referred anyone yet. Share your referral link to start building your network and earning commissions.
                                        </Typography>
                                    </Box>
                                )}
                            
                            </CardContent>
            </Card>
                ) : (
                <Card sx={{
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(30, 41, 59, 0.8) 100%)',
                    boxShadow: '0 8px 32px rgba(148, 163, 184, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    overflow: 'hidden',
                    position: 'relative'
                }}>
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: 'linear-gradient(90deg, #a855f7, #ec4899, #a855f7)',
                        backgroundSize: '200% 100%',
                        animation: 'gradientMove 3s linear infinite',
                    }} />

                    <CardContent sx={{ p: 3 }}>
                        <Box display="flex" alignItems="center" mb={3}>
                            <UserPlus
                                size={22}
                                style={{
                                    color: '#ec4899',
                                    filter: 'drop-shadow(0 0 8px rgba(236, 72, 153, 0.7))'
                                }}
                            />
                            <Typography variant="h6" sx={{ ml: 1, color: '#f3f4f6', fontWeight: 'bold' }}>
                                Your Upline
                            </Typography>
                        </Box>

                        {mainUplineData && Object.keys(mainUplineData).length > 0 ? (
                            <Box sx={{
                                borderRadius: '12px',
                                overflow: 'hidden',
                                background: 'rgba(15, 23, 42, 0.5)',
                                border: '1px solid rgba(255, 255, 255, 0.05)',
                                p: 3
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Avatar
                                        src={mainUplineData?.avatar}
                                        alt={mainUplineData?.username}
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            border: '2px solid rgba(236, 72, 153, 0.3)',
                                            boxShadow: '0 0 15px rgba(236, 72, 153, 0.3)'
                                        }}
                                    />
                                    <Box sx={{ ml: 3 }}>
                                        <Typography sx={{
                                            fontWeight: 'bold',
                                            fontSize: '18px',
                                            color: '#f3f4f6',
                                            mb: 0.5
                                        }}>
                                            {capitalizeFirstLetter(mainUplineData?.username)}
                                        </Typography>
                                        <Typography sx={{
                                            fontSize: '14px',
                                            color: '#9ca3af',
                                            mb: 1.5
                                        }}>
                                            Your direct upline
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Chip
                                                label="Active"
                                                size="small"
                                                sx={{
                                                    height: '22px',
                                                    fontSize: '11px',
                                                    borderRadius: '6px',
                                                    backgroundColor: 'rgba(16, 185, 129, 0.2)',
                                                    color: '#10b981',
                                                    border: '1px solid rgba(16, 185, 129, 0.3)',
                                                    '& .MuiChip-label': {
                                                        padding: '0 8px',
                                                    }
                                                }}
                                            />
                                            <Typography sx={{
                                                fontSize: '12px',
                                                color: '#6b7280',
                                                ml: 2,
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}>
                                                <span style={{
                                                    display: 'inline-block',
                                                    width: '8px',
                                                    height: '8px',
                                                    borderRadius: '50%',
                                                    backgroundColor: 'rgba(107, 114, 128, 0.5)',
                                                    marginRight: '6px'
                                                }}></span>
                                                Joined {niceDate(mainUplineData?.date)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 8,
                                textAlign: 'center'
                            }}>
                                <UserPlus size={48} style={{ color: '#4b5563', marginBottom: '16px' }} />
                                <Typography variant="h6" sx={{ color: '#9ca3af', fontWeight: 'bold', mb: 1 }}>
                                    No Upline Found
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#6b7280', maxWidth: '400px' }}>
                                    You don't have an upline. You're at the top of your network!
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
          )}

           
            </div>
        </div>
    </WrapUpP2p >
  );
}